import { useState, useEffect, useCallback, useMemo } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import {
  Receipt, AlertTriangle, Search, RefreshCw, Loader2, Wallet, Users, Building2,
  CreditCard, Filter, CheckCircle, LayoutDashboard, ClipboardList, Download,
  ChevronDown, TrendingUp, PieChart as PieChartIcon,
} from 'lucide-react'
import { ApiService } from '@/services/api'
import { parseDate, createDateRange, formatDateForInput, formatDateForAPI } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import { useCurrency } from '@/hooks/useCurrency'

interface ExpenseEntry {
  _id: string
  date: string
  name: string
  description: string
  amount: number
  category: string
  paymentType: string
  createdAt: string
}

interface CategoryBreakdown {
  category: string
  count: number
  totalAmount: number
}

interface PaymentTypeBreakdown {
  paymentType: string
  count: number
  totalAmount: number
}

interface ExpenseReportData {
  summary: { totalExpenses: number; totalAmount: number }
  expenses: ExpenseEntry[]
  byCategory: CategoryBreakdown[]
  byPaymentType: PaymentTypeBreakdown[]
}

const STAFF_CATEGORY_NAMES = new Set(['staff', 'salary', 'salaries'])
const UTILITY_SUBCATEGORIES = new Set(['electricity', 'water', 'gas', 'internet', 'wifi', 'broadband', 'fuel'])

// Fixed categorical order — reused from ProfitReportScreen so charts read as
// one system across report screens.
const CATEGORY_COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#14b8a6', '#f43f5e', '#f97316', '#84cc16']

// Payment method identity stays fixed regardless of amount rank, so the same
// method never repaints when the mix shifts across date ranges.
const PAYMENT_COLORS: Record<string, string> = {
  'cash': '#10b981',
  'counter cash': '#06b6d4',
  'upi': '#8b5cf6',
  'card': '#f97316',
  'bank transfer': '#3b82f6',
}

function isStaffCategory(category: string): boolean {
  return STAFF_CATEGORY_NAMES.has((category || '').trim().toLowerCase())
}

function getCategoryDisplay(category: string): { primary: string; secondary?: string } {
  const key = (category || '').trim().toLowerCase()
  if (UTILITY_SUBCATEGORIES.has(key)) return { primary: 'Utilities', secondary: category }
  return { primary: category || 'Uncategorized' }
}

function getPaymentColor(paymentType: string, fallbackIndex: number): string {
  const key = (paymentType || '').trim().toLowerCase()
  return PAYMENT_COLORS[key] || CATEGORY_COLORS[fallbackIndex % CATEGORY_COLORS.length]
}

function formatDisplayDate(dateVal: string | Date | undefined): string {
  if (!dateVal) return '-'
  try {
    return new Date(dateVal).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    })
  } catch {
    return String(dateVal)
  }
}

function CategoryTooltip({ active, payload, total }: any) {
  const { formatCurrency } = useCurrency()
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  const pct = total > 0 ? (p.value / total) * 100 : 0
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 text-sm">
      <p className="font-semibold text-gray-800 dark:text-gray-100">{p.name}</p>
      <p className="text-gray-600 dark:text-gray-300">{formatCurrency(p.value)}</p>
      <p className="text-gray-500 text-xs mt-0.5">{pct.toFixed(1)}% of total · {p.count} record{p.count !== 1 ? 's' : ''}</p>
    </div>
  )
}

function PaymentTooltip({ active, payload }: any) {
  const { formatCurrency } = useCurrency()
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 text-sm">
      <p className="font-semibold text-gray-800 dark:text-gray-100">{p.name}</p>
      <p className="text-gray-600 dark:text-gray-300">{formatCurrency(p.amount)}</p>
      <p className="text-gray-500 text-xs mt-0.5">{p.count} expense{p.count !== 1 ? 's' : ''}</p>
    </div>
  )
}

function TrajectoryTooltip({ active, payload, label }: any) {
  const { formatCurrency } = useCurrency()
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 text-sm">
      <p className="font-semibold text-gray-800 dark:text-gray-100">{label}</p>
      <p className="text-gray-600 dark:text-gray-300">{formatCurrency(payload[0].value)}</p>
    </div>
  )
}

export default function ExpenseScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { currentRestaurant } = useRestaurant()
  const [reportData, setReportData] = useState<ExpenseReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [paymentFilter, setPaymentFilter] = useState('all')
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [activeTab, setActiveTab] = useState<'dashboard' | 'operating'>('dashboard')

  const loadReport = useCallback(async () => {
    if (!currentRestaurant?._id) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getExpenseReport(currentRestaurant._id, dateRange)
      if (response.success) setReportData(response.data)
      else setError(response.message || 'Failed to load expenses')
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as any).response?.data?.message || 'Failed to load expenses'
        : 'Failed to load expenses'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [currentRestaurant?._id, dateRange])

  useEffect(() => {
    if (currentRestaurant?._id) loadReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant?._id, dateRange])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    const newDate = parseDate(value)
    setDateRange(prev =>
      field === 'from' ? createDateRange(newDate, prev.to) : createDateRange(prev.from, newDate)
    )
  }

  const { summary, expenses, byCategory, byPaymentType } = reportData ?? {
    summary: { totalExpenses: 0, totalAmount: 0 },
    expenses: [],
    byCategory: [],
    byPaymentType: []
  }

  const derived = useMemo(() => {
    const staffAmount = byCategory.filter(c => isStaffCategory(c.category)).reduce((s, c) => s + c.totalAmount, 0)
    const operatingAmount = summary.totalAmount - staffAmount
    const operatingExpenses = expenses.filter(e => !isStaffCategory(e.category))

    const donutData = byCategory.map((c, i) => ({
      name: c.category, value: c.totalAmount, count: c.count, color: CATEGORY_COLORS[i % CATEGORY_COLORS.length]
    }))

    const paymentData = byPaymentType.map((p, i) => ({
      name: p.paymentType, amount: p.totalAmount, count: p.count, color: getPaymentColor(p.paymentType, i)
    }))

    const days: { key: string; label: string; amount: number }[] = []
    const cursor = new Date(dateRange.from)
    cursor.setHours(0, 0, 0, 0)
    const end = new Date(dateRange.to)
    end.setHours(0, 0, 0, 0)
    while (cursor <= end) {
      days.push({
        key: formatDateForInput(cursor),
        label: cursor.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
        amount: 0,
      })
      cursor.setDate(cursor.getDate() + 1)
    }
    const dayIndex = new Map(days.map((d, i) => [d.key, i]))
    expenses.forEach(e => {
      const idx = dayIndex.get(formatDateForInput(new Date(e.date)))
      if (idx !== undefined) days[idx].amount += e.amount
    })
    const daysAnalyzed = days.length
    const averageDailyCost = daysAnalyzed > 0 ? summary.totalAmount / daysAnalyzed : 0

    return { staffAmount, operatingAmount, operatingExpenses, donutData, paymentData, costTrajectory: days, daysAnalyzed, averageDailyCost }
  }, [summary, expenses, byCategory, byPaymentType, dateRange])

  const categoryFilterOptions = useMemo(
    () => Array.from(new Set(derived.operatingExpenses.map(e => e.category))).sort((a, b) => a.localeCompare(b)),
    [derived.operatingExpenses]
  )
  const paymentFilterOptions = useMemo(
    () => Array.from(new Set(derived.operatingExpenses.map(e => e.paymentType))).sort((a, b) => a.localeCompare(b)),
    [derived.operatingExpenses]
  )

  const filteredOperating = derived.operatingExpenses.filter(e => {
    const matchesSearch = !searchTerm ||
      e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.paymentType?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || e.category === categoryFilter
    const matchesPayment = paymentFilter === 'all' || e.paymentType === paymentFilter
    return matchesSearch && matchesCategory && matchesPayment
  })

  const exportCSV = (scope: 'all' | 'operating') => {
    if (!reportData) return
    const rows = scope === 'all' ? reportData.expenses : derived.operatingExpenses
    const headers = ['Date', 'Category', 'Paid To / Supplier', 'Amount', 'Payment Method', 'Notes']
    const csvRows = rows.map(e => [
      formatDateForAPI(new Date(e.date)),
      e.category,
      e.name || '',
      e.amount.toFixed(2),
      e.paymentType,
      (e.description || '').replace(/[\r\n,]+/g, ' '),
    ])
    const csv = [headers, ...csvRows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `expenses-${scope}-${formatDateForAPI(dateRange.from)}-to-${formatDateForAPI(dateRange.to)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  // Full-page loader only on first load: swapping the tree out on every date
  // change would unmount the date modal and close it mid-selection.
  if (isLoading && !reportData) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2 text-muted-foreground">Loading expenses...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <div className="text-center space-y-1">
          <p className="font-semibold text-destructive">Failed to load expenses</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
        <Button variant="outline" onClick={loadReport} disabled={isLoading}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex-shrink-0">
            <Receipt className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Expense & Cost Analysis</h1>
            <p className="text-muted-foreground text-sm mt-0.5 hidden sm:block">
              Understand spending, payment distribution, payroll and operating expenses
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'dashboard' | 'operating')}>
          <TabsList>
            <TabsTrigger value="dashboard" className="gap-1.5">
              <LayoutDashboard className="h-4 w-4" />
              Executive Dashboard
            </TabsTrigger>
            <TabsTrigger value="operating" className="gap-1.5">
              <ClipboardList className="h-4 w-4" />
              Operating Expenses
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filters & Actions */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div className="hidden md:flex items-center gap-2 flex-1 min-w-0">
            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <DateRangeModal
              dateRange={dateRange}
              onDateChange={handleDateChange}
              onApply={loadReport}
              title="Expense Report Date Range"
              description="Select the date range for your expense report"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={loadReport} variant="outline" size="icon" disabled={isLoading} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-1.5" />
                  Export
                  <ChevronDown className="h-4 w-4 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportCSV('all')}>All Expenses (CSV)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportCSV('operating')}>Operating Expenses Only (CSV)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={loadReport}
        title="Expense Report Date Range"
        description="Select the date range for your expense report"
      />

      {/* Summary Cards */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Spent</CardTitle>
            <div className="p-1.5 rounded-full bg-gray-900 dark:bg-gray-100">
              <Wallet className="h-3.5 w-3.5 text-white dark:text-gray-900" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary.totalAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">All recorded costs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Staff Salaries</CardTitle>
            <Users className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(derived.staffAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">Recorded under Staff category</p>
          </CardContent>
        </Card>

        <Card className="col-span-2 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Operating Expenses</CardTitle>
            <Building2 className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">{formatCurrency(derived.operatingAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">Utilities, Rent, Etc</p>
          </CardContent>
        </Card>
      </div>

      {summary.totalExpenses === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-medium">No expenses found for this period</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'dashboard' | 'operating')}>

          {/* ─── EXECUTIVE DASHBOARD ─── */}
          <TabsContent value="dashboard" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Expenses by Category */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <PieChartIcon className="h-4 w-4 text-purple-500" />
                    Expenses by Category
                  </CardTitle>
                  <CardDescription className="text-xs">Total spending by department</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={derived.donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                        {derived.donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip content={(props: any) => <CategoryTooltip {...props} total={summary.totalAmount} />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 mt-2">
                    {derived.donutData.map(d => (
                      <div key={d.name} className="flex items-center gap-2 text-sm">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                        <span className="text-muted-foreground truncate flex-1">{d.name}</span>
                        <span className="font-medium">{formatCurrency(d.value)}</span>
                        <span className="text-xs text-muted-foreground w-10 text-right">
                          ({summary.totalAmount > 0 ? ((d.value / summary.totalAmount) * 100).toFixed(0) : 0}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Payment Methods */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CreditCard className="h-4 w-4 text-blue-500" />
                    Payment Methods
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {derived.paymentData.map(p => p.name).join(', ') || 'No payments recorded'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={derived.paymentData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barCategoryGap="35%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${currencySymbol}${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} />
                      <Tooltip content={<PaymentTooltip />} />
                      <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                        {derived.paymentData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {derived.paymentData.map(d => (
                      <div key={d.name} className="border rounded-lg p-2.5 text-center">
                        <div className="text-xs text-muted-foreground mb-0.5 truncate">{d.name}</div>
                        <div className="text-sm font-bold" style={{ color: d.color }}>{formatCurrency(d.amount)}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cost Trajectory */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-4 w-4 text-indigo-500" />
                    Cost Trajectory
                  </CardTitle>
                  <CardDescription className="text-xs">Daily spending trend over period</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={derived.costTrajectory} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10 }}
                        interval={Math.max(0, Math.ceil(derived.costTrajectory.length / 6) - 1)}
                      />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${currencySymbol}${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} />
                      <Tooltip content={<TrajectoryTooltip />} />
                      <Line
                        type="monotone" dataKey="amount" stroke="#6366f1" strokeWidth={2} dot={false}
                        activeDot={{ r: 4, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Average Daily Cost</p>
                      <p className="font-bold text-indigo-600">{formatCurrency(derived.averageDailyCost)} / day</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Days Analyzed</p>
                      <p className="font-bold">{derived.daysAnalyzed} days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ─── OPERATING EXPENSES ─── */}
          <TabsContent value="operating" className="mt-0">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Receipt className="h-4 w-4" />
                  Recorded Operating Expenses
                </CardTitle>
                <CardDescription className="text-xs">Utilities, operations, rent, marketing & supplies</CardDescription>
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search ref, paid to, notes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-full sm:w-[170px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categoryFilterOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                    <SelectTrigger className="w-full sm:w-[170px]"><SelectValue placeholder="All Payment Methods" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payment Methods</SelectItem>
                      {paymentFilterOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {/* Desktop Table */}
                <div className="hidden lg:block">
                  <div className="max-h-[600px] overflow-y-auto thin-scrollbar">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background">
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Category & Sub</TableHead>
                          <TableHead>Paid To / Supplier</TableHead>
                          <TableHead>Ref #</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Payment Method</TableHead>
                          <TableHead>Notes & Description</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredOperating.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                              No results match your search
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredOperating.map((e) => {
                            const { primary, secondary } = getCategoryDisplay(e.category)
                            return (
                              <TableRow key={e._id}>
                                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                  {formatDisplayDate(e.date)}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary" className="text-xs">{primary}</Badge>
                                  {secondary && <div className="text-xs text-muted-foreground mt-0.5">{secondary}</div>}
                                </TableCell>
                                <TableCell className="text-sm">{e.name || '-'}</TableCell>
                                <TableCell className="text-sm text-muted-foreground">–</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(e.amount)}</TableCell>
                                <TableCell><Badge variant="outline" className="text-xs">{e.paymentType}</Badge></TableCell>
                                <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">
                                  {e.description || '-'}
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3">
                  {filteredOperating.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">No results match your search</div>
                  ) : (
                    filteredOperating.map((e) => {
                      const { primary, secondary } = getCategoryDisplay(e.category)
                      return (
                        <div key={e._id} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{e.name || e.category}</span>
                            <span className="font-bold">{formatCurrency(e.amount)}</span>
                          </div>
                          {e.description && (
                            <p className="text-sm text-muted-foreground">{e.description}</p>
                          )}
                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                            <div>
                              <Badge variant="outline" className="text-xs">{primary}</Badge>
                              {secondary && <span className="ml-1.5">{secondary}</span>}
                            </div>
                            <span>{e.paymentType}</span>
                            <span>{formatDisplayDate(e.date)}</span>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
